/*
The ink illustrations are revealed by a scribble: the image sits at opacity
.22 until it is in view, then a single 1.6s pass of a hand-drawn SVG mask
(34 rows, seeded jitter, rotated -35deg so the sweep runs from the top left)
inks it to full. Ported from the prototype's traceCard().
*/

const ROWS = 34;
const JITTER = 24;
const STROKE = 80;
const DURATION = 1.6;
const BANDS = 4;
const START_DELAY = 0.3;
const ANGLE = -35;
const SEED = 11;

let uid = 0;

function rng(seed) {
    let t = seed;
    return () => {
        t = (t * 1664525 + 1013904223) % 4294967296;
        return t / 4294967296;
    };
}

// Rows of loose zigzag lines across a 1000x1000 box, grouped into bands
function scribble(seed, rows, jit, bands) {
    const random = rng(seed);
    const paths = [];
    const step = 1000 / rows;
    const per = Math.ceil(rows / bands);
    let d = '';

    for (let i = 0; i < rows; i += 1) {
        const y = i * step + step / 2;
        const dir = i % 2 ? 1 : -1;
        const x0 = dir > 0 ? -60 : 1060;
        const x1 = dir > 0 ? 1060 : -60;
        let seg =
            i % per === 0
                ? `M ${x0} ${y + (random() - 0.5) * jit}`
                : ` L ${x0} ${y + (random() - 0.5) * jit}`;

        const n = 6;
        for (let k = 1; k <= n; k += 1) {
            const t = k / n;
            const x = x0 + (x1 - x0) * t;
            const yy = y + (random() - 0.5) * jit;
            seg += ` Q ${x0 + (x1 - x0) * (t - 0.5 / n)} ${yy + (random() - 0.5) * jit * 1.5} ${x} ${yy}`;
        }

        d += seg;

        if ((i + 1) % per === 0 || i === rows - 1) {
            paths.push(d);
            d = '';
        }
    }

    return paths;
}

function buildSvg(src, height, seed, hold) {
    uid += 1;
    const id = `trace-mask-${uid}`;
    const per = DURATION / BANDS;
    const paths = scribble(seed, ROWS, JITTER, BANDS);
    // Map the square scribble field onto the image box, then rotate it
    const transform = `scale(1 ${height / 1000}) translate(500 500) rotate(${ANGLE}) scale(1.45) translate(-500 -500)`;
    const strokes = paths
        .map(
            (d, i) =>
                `<path class="mp" d="${d}" stroke-width="${STROKE}" pathLength="1" stroke-dasharray="1" stroke-dashoffset="1" style="animation:trace-draw ${per}s linear ${
                    START_DELAY + i * per
                }s forwards${hold ? ';animation-play-state:paused' : ''}"/>`
        )
        .join('');

    return (
        `<svg class="trace-svg" viewBox="0 0 1000 ${height}" preserveAspectRatio="xMidYMid meet" aria-hidden="true" focusable="false">` +
        `<mask id="${id}" maskUnits="userSpaceOnUse" x="-500" y="-500" width="2000" height="${height + 1000}">` +
        `<g transform="${transform}">${strokes}</g></mask>` +
        `<image href="${src}" width="1000" height="${height}" mask="url(#${id})"/></svg>`
    );
}

function reveal(img, options) {
    const opts = options || {};
    const src = img.currentSrc || img.src;

    if (!src || img.dataset.traced) {
        return;
    }

    img.dataset.traced = 'true';

    const width = img.naturalWidth || 1000;
    const natural = img.naturalHeight || 1000;
    const height = Math.max(1, Math.round((1000 * natural) / width));
    const target = img.closest('picture') || img;

    const wrapper = document.createElement('div');
    wrapper.className = 'trace is-dimmed';
    target.parentNode.insertBefore(wrapper, target);
    wrapper.appendChild(target);
    wrapper.insertAdjacentHTML('beforeend', buildSvg(src, height, opts.seed || SEED, !opts.immediate));

    const total = (START_DELAY + DURATION + 0.2) * 1000;

    function play() {
        wrapper.querySelectorAll('.mp').forEach((path) => {
            path.style.animationPlayState = 'running';
        });

        setTimeout(() => {
            // Un-dim under the overlay first, then drop the overlay once the
            // image has faded up — otherwise the last frame dips to 22%
            wrapper.classList.remove('is-dimmed');
            setTimeout(() => {
                const svg = wrapper.querySelector('.trace-svg');
                if (svg) {
                    svg.remove();
                }
            }, 350);
        }, total);
    }

    if (opts.immediate || typeof IntersectionObserver === 'undefined') {
        play();
        return;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    observer.disconnect();
                    play();
                }
            });
        },
        {threshold: 0.35}
    );

    observer.observe(wrapper);
}

function whenLoaded(img, options) {
    if (img.complete && img.naturalWidth) {
        reveal(img, options);
    } else {
        img.addEventListener('load', () => reveal(img, options), {once: true});
    }
}

export default function traceReveal() {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    // The header illustration is above the fold, so it starts on load
    // Same for the home hero portrait
    document.querySelectorAll('.gh-feature-image img, .gh-home-art img').forEach((img) => {
        whenLoaded(img, {immediate: true});
    });

    // In-article images opt in with the internal tag #reveal
    if (document.querySelector('.gh-article[data-reveal]')) {
        document.querySelectorAll('.gh-content .kg-image-card img').forEach((img, i) => {
            whenLoaded(img, {seed: SEED + i * 3});
        });
    }
}
